import { useEffect, useRef } from 'react';
import {
    invalidateDepartmentData,
    invalidateEmployeeData,
    invalidatePatternTemplateData,
    invalidatePositionData,
    invalidateShiftTypeData,
    invalidateTraitData,
    useActiveDepartmentStore,
} from '@stores';
import { useDepartment } from '@hooks';

/*
 * Keeps the active department UI store in sync with the routed department
 * and drops department-scoped cached data when the department changes,
 * bounding cache memory and never serving one department's data under
 * another (keys already differ — this is defensive).
 */
const useActiveDepartmentStoreUpdate = (): void => {
    const { department } = useDepartment();

    const departmentId = department?.id ?? null;

    const setActiveDepartmentId = useActiveDepartmentStore(
        (state) => state.setActiveDepartmentId,
    );
    const setIsReadOnly = useActiveDepartmentStore(
        (state) => state.setIsDepartmentReadOnly,
    );

    useEffect(() => {
        setActiveDepartmentId(departmentId);

        if (department) {
            setIsReadOnly(!department.allowedToUpdate);
        }
    }, [department, departmentId, setActiveDepartmentId, setIsReadOnly]);

    const previousDepartmentIdRef = useRef<string | null>(null);

    useEffect(() => {
        const hasDepartmentChanged =
            previousDepartmentIdRef.current !== null &&
            previousDepartmentIdRef.current !== departmentId;

        if (hasDepartmentChanged) {
            invalidateEmployeeData();
            invalidateShiftTypeData();
            invalidatePatternTemplateData();
            invalidateDepartmentData();
            invalidatePositionData();
            invalidateTraitData();
        }

        previousDepartmentIdRef.current = departmentId;
    }, [departmentId]);
};

export default useActiveDepartmentStoreUpdate;
